/* Presentation only: canonical option inputs remain owned by xVariantSelectCard. */
(() => {
  if (customElements.get('card-compact-picker')) return;
  let activePicker;

  class CardCompactPicker extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-compact-panel]');
      this.row = this.querySelector('[data-compact-row]');
      this.overlay = this.querySelector('[data-compact-overlay]');
      this.card = this.closest('[data-inline-product-card]');
      if (!this.panel || !this.row || !this.card || this.controller) return;
      this.removeAttribute('data-ready');
      this.controller = new AbortController();
      const on = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: this.controller.signal });
      this.breakpoint = matchMedia('(min-width: 768px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.values = [...this.row.querySelectorAll('[data-compact-value]')];
      this.expand = this.querySelector('[data-compact-expand]');
      this.tooltip = this.querySelector('[data-compact-tooltip]');
      this.error = this.querySelector('[data-compact-error]');
      on(this, 'click', event => {
        const value = event.target.closest('[data-compact-value]');
        if (value) {
          event.preventDefault();
          event.stopPropagation();
          this.stopHint();
          const input = this.inputFor(value);
          // Native click preserves the radio group's behavior and Alpine change handler.
          if (input && !input.checked) input.click();
          this.sync();
        } else if (event.target.closest('[data-compact-expand]')) {
          event.preventDefault();
          event.stopPropagation();
          this.open(event.target.closest('[data-compact-expand]'));
        } else if (event.target.closest('[data-compact-close]')) {
          event.preventDefault();
          this.close();
        } else if (event.target === this.overlay) this.close();
      });
      on(this, 'pointerdown', () => this.stopHint());
      on(this, 'focusin', () => this.stopHint());
      on(this.panel, 'cancel', event => { event.preventDefault(); this.close(); });
      on(this.panel, 'close', () => { if (!this.panel.open) this.finishClose(false); });
      const swatchFor = target => {
        const label = target.closest('.card-product-color-option') || (target.matches('input[type="radio"]') ? target.nextElementSibling : null);
        return label?.classList.contains('card-product-color-option') ? label : null;
      };
      on(this.panel, 'pointerover', event => { const swatch = swatchFor(event.target); if (swatch) this.showTooltip(swatch); });
      on(this.panel, 'pointerout', event => {
        const swatch = swatchFor(event.target);
        if (swatch && !swatch.contains(event.relatedTarget)) this.hideTooltip();
      });
      on(this.panel, 'focusin', event => { const swatch = swatchFor(event.target); if (swatch && !this.focusingPanel && event.target.matches(':focus-visible')) this.showTooltip(swatch); });
      on(this.panel, 'focusout', () => this.hideTooltip());
      on(this.panel, 'keydown', event => {
        if (event.key !== 'Tab' || this.mode !== 'modal') return;
        const controls = [...this.panel.querySelectorAll('button, input, select, textarea, a[href], [tabindex]')].filter(control => {
          if (control.disabled || control.tabIndex < 0 || !control.getClientRects().length) return false;
          if (control.type !== 'radio') return true;
          const group = [...this.panel.querySelectorAll('input[type="radio"]')].filter(input => input.name === control.name);
          return control === (group.find(input => input.checked) || group[0]);
        });
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
      on(this.panel, 'pointerdown', event => { this.backdropDown = this.outsidePanel(event); });
      on(this.panel, 'click', event => {
        if (this.mode === 'modal' && this.backdropDown && this.outsidePanel(event)) this.close();
        this.backdropDown = false;
      });
      on(document, 'pointerdown', event => {
        if (this.opened && this.mode === 'card' && !this.overlay.contains(event.target)) this.close(false);
      });
      on(document, 'focusin', event => {
        if (this.opened && this.mode === 'card' && !this.panel.contains(event.target)) this.close(false);
      });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.opened) { event.preventDefault(); this.close(); }
      });
      on(this, 'change', event => {
        this.sync();
      });
      on(window, 'resize', () => { this.hideTooltip(); this.scheduleLayout(); });
      on(document, 'scroll', () => { this.hideTooltip(); this.position(); }, { capture: true, passive: true });
      on(this.breakpoint, 'change', () => { this.close(false, true); this.scheduleLayout(); });
      on(this.reducedMotion, 'change', () => {
        this.stopAnimations(); this.stopHint();
        if (this.closing) this.finishClose(false);
        this.sheetAnimation?.cancel();
        this.sheetAnimation = null;
      });
      this.values.forEach(pill => {
        on(pill, 'pointerenter', () => this.animateText(pill));
        on(pill, 'pointerleave', () => { if (document.activeElement !== pill) this.stopAnimation(pill); });
        on(pill, 'focus', () => this.animateText(pill));
        on(pill, 'blur', () => this.stopAnimation(pill));
      });
      this.observer = new ResizeObserver(() => this.scheduleLayout());
      this.observer.observe(this.row);
      this.observer.observe(this.card);
      // Track content shifts even when the grid keeps cards at an equal height.
      this.contentObserver = new MutationObserver(records => {
        if (records.some(record => !this.row.contains(record.target))) this.scheduleLayout();
      });
      this.contentObserver.observe(this.card, { childList: true, subtree: true, characterData: true });
      this.hintObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.hintObserver.disconnect();
        if (!this.hintSeen && !this.reducedMotion.matches) {
          this.hintSeen = true;
          this.hintAnimation = this.expand.animate([
            { backgroundColor: 'rgba(var(--colors-heading), 0)' },
            { backgroundColor: 'rgba(var(--colors-heading), .08)' },
            { backgroundColor: 'rgba(var(--colors-heading), 0)' }
          ], { duration: 1600, iterations: 2 });
        }
      });
      this.sync();
      document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleLayout(); });
    }

    inputFor(pill) { return this.panel.querySelector(`#${CSS.escape(pill.dataset.inputId)}`); }

    sync() {
      if (!this.panel) return;
      this.values.forEach(pill => {
        const input = this.inputFor(pill), label = input?.nextElementSibling;
        if (!input || !label) return;
        pill.setAttribute('aria-pressed', String(input.checked));
        pill.setAttribute('aria-label', `${pill.dataset.optionName}: ${input.getAttribute('aria-label') || input.value}`);
        pill.classList.toggle('is-unavailable', input.classList.contains('disabled'));
        const swatch = label.classList.contains('color-watches');
        pill.classList.toggle('card-compact-swatch', swatch);
        pill.classList.toggle('product-card-radius-control', !swatch);
        if (swatch) {
          // Copy visual markup only, never Alpine handlers, inputs, IDs or labels.
          const preview = document.createElement('span');
          preview.className = label.className + ' card-compact-swatch-preview';
          preview.classList.remove('active-swatch');
          preview.style.cssText = label.style.cssText;
          preview.dataset.swatch = label.dataset.swatch || '';
          const tooltip = label.querySelector('.option-value-tooltip')?.cloneNode(true);
          if (tooltip) {
            for (const node of [tooltip, ...tooltip.querySelectorAll('*')]) {
              for (const attr of [...node.attributes]) {
                if (attr.name.startsWith('x-') || attr.name.startsWith('@') || attr.name.startsWith(':') || attr.name === 'id') node.removeAttribute(attr.name);
              }
            }
            tooltip.style.setProperty('--maxwidth-tooltip', '200px');
            preview.append(tooltip);
          }
          const mask = document.createElement('span');
          mask.className = 'card-product-swatch-disabled color-watches-disable';
          preview.append(mask);
          pill.replaceChildren(preview);
        } else {
          const text = label.querySelector('.card-product-option-text')?.textContent.trim() || input.value;
          const target = pill.querySelector('[data-compact-text]');
          if (target && target.textContent !== text) { this.stopAnimation(pill); target.textContent = text; }
        }
      });
      const state = this.card.dataset.variantState;
      const price = this.card.querySelector('.x-card-price'), priceCopy = price?.cloneNode(true);
      priceCopy?.querySelectorAll('.hidden, [hidden], s, del, script, style').forEach(node => node.remove());
      if (state !== 'pending') this.querySelector('[data-compact-price]').textContent = price && !price.classList.contains('hidden') ? priceCopy.textContent.trim().replace(/\s+/g, ' ') : '';
      this.panel.setAttribute('aria-busy', String(state === 'pending'));
      if (state !== this.feedbackState) {
        clearTimeout(this.loadingTimer);
        this.loadingVisible = false;
        this.feedbackState = state;
        if (state === 'pending') this.loadingTimer = setTimeout(() => {
          if (!this.isConnected || this.card.dataset.variantState !== 'pending') return;
          this.loadingVisible = true;
          this.updateFeedback();
        }, 150);
      }
      this.updateFeedback();
      this.scheduleLayout();
    }

    updateFeedback() {
      const surface = this.opened ? 'panel' : 'inline';
      const state = this.card.dataset.variantState;
      this.querySelectorAll('[data-compact-loading]').forEach(indicator => {
        indicator.toggleAttribute('data-visible', state === 'pending' && !!this.loadingVisible && indicator.dataset.compactLoading === surface);
      });
      this.querySelectorAll('[data-compact-messages]').forEach(messages => {
        const active = messages.dataset.compactMessages === surface;
        messages.hidden = !active;
        const status = messages.querySelector('[role="status"]');
        status.setAttribute('aria-live', active ? 'polite' : 'off');
        status.classList.toggle('card-compact-announcement', state === 'pending');
        const text = !active ? '' : state === 'pending' && this.loadingVisible ? this.dataset.updating : state === 'unavailable' ? this.card.querySelector('[data-inline-card-atc]')?.getAttribute('aria-label') || '' : '';
        if (status.textContent !== text) status.textContent = text;
        // Move the single Alpine-owned error/Retry control within the same scope.
        if (active && this.error && this.error.parentElement !== messages) messages.append(this.error);
      });
    }

    showTooltip(swatch) {
      if (!this.opened || this.closing) return;
      this.tooltip.textContent = swatch.dataset.name || swatch.getAttribute('aria-label') || '';
      this.tooltip.hidden = false;
      const panel = this.panel.getBoundingClientRect(), anchor = swatch.getBoundingClientRect();
      const scaleX = panel.width / this.panel.offsetWidth || 1, scaleY = panel.height / this.panel.offsetHeight || 1;
      const width = this.tooltip.offsetWidth, height = this.tooltip.offsetHeight;
      const x = (anchor.left + anchor.width / 2 - panel.left) / scaleX - this.panel.clientLeft - width / 2;
      let y = (anchor.bottom - panel.top) / scaleY - this.panel.clientTop + 6;
      if (y + height > this.panel.clientHeight - 8) y = (anchor.top - panel.top) / scaleY - this.panel.clientTop - height - 6;
      Object.assign(this.tooltip.style, {
        left: `${Math.max(8, Math.min(x, this.panel.clientWidth - width - 8))}px`,
        top: `${Math.max(8, Math.min(y, this.panel.clientHeight - height - 8))}px`
      });
    }

    hideTooltip() { if (this.tooltip) this.tooltip.hidden = true; }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.layout(); this.position();
      });
    }

    layout() {
      if (!this.row.clientWidth) return;
      const focused = document.activeElement;
      const count = this.querySelector('[data-compact-count]');
      this.values.forEach(pill => { pill.hidden = false; });
      const widths = new Map(this.values.map(pill => [pill, pill.offsetWidth]));
      const groups = [...new Set(this.values.map(pill => pill.dataset.groupIndex))].map(index => this.values.filter(pill => pill.dataset.groupIndex === index));
      const selected = groups.flatMap(group => group.filter(pill => pill.getAttribute('aria-pressed') === 'true'));
      const remaining = groups.map(group => group.filter(pill => !selected.includes(pill)));
      const priority = [...selected];
      for (let i = 0; remaining.some(group => i < group.length); i++) {
        remaining.forEach(group => { if (group[i]) priority.push(group[i]); });
      }
      const gap = parseFloat(getComputedStyle(this.row).columnGap) || 0;
      const groupLimit = groups.length === 1 ? this.values.length : groups.length === 2 ? 2 : 1;
      // Reserve the exact count-button width, including digit-boundary changes.
      let visible = [];
      for (let target = Math.min(priority.length, groups.length * groupLimit); target >= 0; target--) {
        const hidden = this.values.length - target;
        count.hidden = !hidden;
        count.textContent = hidden ? `+${hidden}` : '';
        let space = this.row.clientWidth - this.expand.offsetWidth;
        const fit = [];
        const counts = new Map();
        for (const pill of priority) {
          const group = pill.dataset.groupIndex;
          if ((counts.get(group) || 0) >= groupLimit) continue;
          if (widths.get(pill) + gap <= space) {
            fit.push(pill);
            counts.set(group, (counts.get(group) || 0) + 1);
            space -= widths.get(pill) + gap;
          }
        }
        if (fit.length >= target) { visible = fit.slice(0, target); break; }
      }
      // Pack selected values first, but display each group's shortcuts together.
      visible = groups.flatMap(group => priority.filter(pill => group.includes(pill) && visible.includes(pill)));
      this.values.forEach(pill => { pill.hidden = !visible.includes(pill); });
      // DOM order matches visual and keyboard order; avoid moving unchanged nodes.
      visible.forEach((pill, index) => {
        if (this.row.children[index] !== pill) this.row.insertBefore(pill, this.row.children[index]);
      });
      if (this.row.lastElementChild !== this.expand) this.row.append(this.expand);
      // Reveal only after visuals, packing and the omitted-value count are final.
      if (!this.hasAttribute('data-ready')) {
        this.setAttribute('data-ready', '');
        if (!this.hintSeen) this.hintObserver.observe(this.expand);
      }
      if (this.values.includes(focused)) (focused.hidden ? this.expand : focused).focus({ preventScroll: true });
    }

    animateText(pill) {
      this.stopAnimation(pill);
      const text = pill.querySelector('[data-compact-text]');
      if (this.reducedMotion.matches || !text) return;
      const distance = text.scrollWidth - text.parentElement.clientWidth;
      if (distance <= 1) return;
      const direction = getComputedStyle(pill).direction === 'rtl' ? 1 : -1;
      pill.textAnimation = text.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${direction * distance}px)` }], { delay: 400, duration: distance / 30 * 1000, easing: 'linear', fill: 'forwards' });
      pill.animationTimer = setTimeout(() => pill.setAttribute('data-animating', ''), 400);
    }

    stopAnimation(pill) {
      clearTimeout(pill.animationTimer); pill.textAnimation?.cancel(); pill.removeAttribute('data-animating');
    }
    stopAnimations() { this.values?.forEach(pill => this.stopAnimation(pill)); }
    stopHint() { this.hintSeen = true; this.hintAnimation?.cancel(); this.hintObserver?.disconnect(); }

    animateSheet(opening) {
      this.sheetAnimation?.cancel();
      if (this.reducedMotion.matches) return;
      this.sheetAnimation = this.panel.animate([
        { transform: opening ? 'translateY(100%)' : 'translateY(0)' },
        { transform: opening ? 'translateY(0)' : 'translateY(100%)' }
      ], { duration: 220, easing: opening ? 'ease-out' : 'ease-in', fill: 'both' });
      return this.sheetAnimation;
    }

    open(trigger) {
      this.stopHint();
      if (activePicker && activePicker !== this) activePicker.close(false, true);
      if (this.closing) this.finishClose(false);
      if (this.opened) return;
      this.invoker = trigger; this.sync(); this.opened = true; activePicker = this;
      this.mode = this.breakpoint.matches ? 'card' : 'modal';
      this.overlay.dataset.open = '';
      if (this.mode === 'card') { this.position(); this.panel.show(); }
      else {
        this.panel.showModal();
        this.scrollOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
      this.expand.setAttribute('aria-expanded', 'true');
      this.updateFeedback();
      const target = this.panel.querySelector('input:checked') || this.panel.querySelector('[data-compact-close]');
      this.focusingPanel = true;
      target?.focus({ preventScroll: true });
      this.focusingPanel = false;
      this.panel.querySelector('.card-compact-body').scrollTop = 0;
      this.animateSheet(true);
    }

    position() {
      if (!this.opened || this.mode !== 'card') return;
      // Convert the card's inner bounds to the nested overlay's offset parent.
      // Keeping everything in the card preserves Alpine scope and carousel clipping.
      const parent = this.overlay.offsetParent;
      if (!parent) return;
      const rect = this.card.getBoundingClientRect(), origin = parent.getBoundingClientRect();
      const scaleX = origin.width / parent.offsetWidth || 1, scaleY = origin.height / parent.offsetHeight || 1;
      Object.assign(this.overlay.style, {
        left: `${(rect.left - origin.left) / scaleX + this.card.clientLeft - parent.clientLeft + parent.scrollLeft}px`,
        top: `${(rect.top - origin.top) / scaleY + this.card.clientTop - parent.clientTop + parent.scrollTop}px`,
        width: `${this.card.clientWidth}px`, height: `${this.card.clientHeight}px`,
        borderRadius: getComputedStyle(this.card).borderRadius
      });
    }

    outsidePanel(event) {
      if (event.target !== this.panel) return false;
      const rect = this.panel.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    close(restoreFocus = true, immediate = false) {
      if (!this.opened) return;
      if (this.closing) { if (immediate) this.finishClose(restoreFocus); return; }
      this.closing = true;
      this.hideTooltip();
      const animation = !immediate && this.animateSheet(false);
      if (animation) animation.finished.then(() => this.finishClose(restoreFocus), () => {});
      else this.finishClose(restoreFocus);
    }

    finishClose(restoreFocus = true) {
      if (!this.opened) return;
      this.opened = false; this.closing = false;
      this.sheetAnimation?.cancel();
      if (this.panel.open) this.panel.close();
      this.sheetAnimation = null;
      delete this.overlay.dataset.open;
      if (activePicker === this) activePicker = null;
      if (this.mode === 'modal') document.documentElement.style.overflow = this.scrollOverflow;
      this.expand.setAttribute('aria-expanded', 'false');
      this.hideTooltip();
      this.updateFeedback();
      this.stopAnimations();
      if (restoreFocus && this.invoker?.isConnected) this.invoker.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.removeAttribute('data-ready');
      this.finishClose(false); this.stopAnimations(); this.stopHint();
      clearTimeout(this.loadingTimer);
      this.loadingVisible = false;
      this.feedbackState = undefined;
      this.querySelectorAll('[data-compact-loading]').forEach(indicator => indicator.removeAttribute('data-visible'));
      cancelAnimationFrame(this.layoutFrame);
      this.observer?.disconnect(); this.contentObserver?.disconnect();
      this.controller?.abort(); this.controller = null;
    }
  }
  customElements.define('card-compact-picker', CardCompactPicker);
})();
